import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_CHAIN_ID, StreamShapes, routes } from "@sablier/v2-constants";
import { useNextRouterConfirm } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { guards } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { IAutofillParams, IAutofillParamsURL } from "@sablier/v2-types/stream";
import { vendors } from "@sablier/v2-utils";
import { useRouter } from "next/router";
import { useExtensionUtilities } from "~/client/extensions";
import {
  useAccount,
  useCreateSingleField,
  useCreateSingleForm,
  useModalAutofillCreate,
  useModalTransaction,
  useTokenBalance,
} from "~/client/hooks";
import type { StreamShape } from "@sablier/v2-constants";

function useIsChanged() {
  const fields = useCreateSingleForm((state) => state.fields);

  const isChanged = useMemo(() => {
    return (
      !_.isNilOrEmptyString(fields.address.value) ||
      !_.isNilOrEmptyString(fields.amount.value) ||
      !_.isNilOrEmptyString(fields.duration.value) ||
      !_.isNilOrEmptyString(fields.start.value) ||
      !_.isNilOrEmptyString(fields.end.value)
    );
  }, [fields]);

  return isChanged;
}

function useAutofill() {
  const [isNecessary, setIsNecessary] = useState(true);

  const router = useRouter();
  const extend = useExtensionUtilities();
  const update = useCreateSingleForm((state) => state.api.updateFields);
  const { setOpen } = useModalAutofillCreate();
  const { t } = useT();

  const onConfirm = useCallback(
    (parameters: IAutofillParams) => {
      /** Because of its dependencies on other values, the end date has to be validated separately */

      let warningEndDate: string | undefined = undefined;

      if (
        parameters.timing === "range" &&
        !_.isNilOrEmptyString(parameters.end)
      ) {
        if (!_.isNilOrEmptyString(parameters.start)) {
          warningEndDate =
            guards.validateMoment({
              t,
              value: parameters.end,
              min: parameters.start,
            }) ||
            guards.validateMoment({
              isExcess: true,
              purpose: "datetoday",
              t,
              value: parameters.end,
              min: new Date().valueOf().toString(),
            });
        }
      }

      const extension = extend.initialize({
        shape: parameters.shape.id,
        cliffDuration: parameters.cliff || "",
        cliffEnd: parameters.cliff || "",
        steps: parameters.steps || "",
        unlock: parameters.unlock || "",
      });

      update({
        shape: {
          value: parameters.shape.id as StreamShape,
        },
        token: {
          value: parameters.token,
        },
        address: {
          value: parameters.recipient,
        },
        cancelability: {
          value: parameters.cancelable,
        },
        amount: {
          value: parameters.amount,
        },
        duration: {
          isActive: parameters.timing === "duration",
          value: parameters.duration,
        },
        start: {
          value: parameters.timing === "range" ? parameters.start : undefined,
        },
        end: {
          value: parameters.timing === "range" ? parameters.end : undefined,
          warning: warningEndDate,
        },
        extension,
      });

      document.getElementById("amount")?.focus();
      document.getElementById("amount")?.blur();
    },
    [extend, t, update],
  );

  const doAutofill = useCallback(() => {
    const { query } = router;
    const identifier = _.get(query, "shape")?.toString();
    const shape = Stream.findShape(identifier);

    /** If the minimal requirements aren't being met, stop the auto-fill preemptively */

    if (shape.isUnknown) {
      return;
    }

    const isDependent = (() => {
      const dependencies: (keyof IAutofillParamsURL)[] = [
        "amount",
        "cancelable",
        "chainId",
        "cliff",
        "end",
        "isDuration",
        "recipient",
        "steps",
        "start",
        "token",
        "unlock",
      ];
      return !!dependencies.find(
        (dependency) => !_.isNilOrEmptyString(_.get(query, dependency)),
      );
    })();

    if (!isDependent) {
      return;
    }

    /** If the auto-fill has already been shown, prevent a second opening */

    if (!isNecessary) {
      return;
    }

    setIsNecessary(false);
    setOpen(true, {
      onConfirm,
    });
  }, [isNecessary, onConfirm, router, setIsNecessary, setOpen]);

  return {
    doAutofill,
  };
}

export function useInit() {
  const router = useRouter();
  const { doAutofill } = useAutofill();
  const extend = useExtensionUtilities();
  const update = useCreateSingleForm((state) => state.api.updateFields);

  useEffect(() => {
    const { isReady, query, replace } = router;

    if (!isReady) {
      return;
    }

    const identifier = _.get(query, "shape")?.toString();
    const shape = Stream.findShape(identifier);
    if (shape.isUnknown) {
      /**
       * The page needs to be set to a known shape before autofill and interactions commence.
       * Use the "linear" variant as a fallback.
       */
      try {
        void replace(
          routes.client.pages.createSingle.builder({
            shape: StreamShapes.linear.id,
          }),
          undefined,
          { shallow: true },
        );
      } catch (error) {
        vendors.crash.log(error);
      }
    } else {
      /** Setup extension eagerly (for when autofill is not needed) */
      const extension = extend.initialize({ shape: shape.id });
      update({
        shape: {
          value: shape.id as StreamShape,
        },
        extension,
      });

      doAutofill();
    }
  }, [doAutofill, extend, router, update]);
}

export function useWatcher() {
  const token = useCreateSingleField("token");
  const update = useCreateSingleForm((state) => state.api.updateFields);
  const reset = useCreateSingleForm((state) => state.api.resetFields);
  const { chainId, isHostSafe } = useAccount();
  const { humanized: balance, isLoading } = useTokenBalance({
    token: token.field.value,
  });

  const { data } = useModalTransaction();

  const isChanged = useIsChanged();

  const isConfirmRequired = useMemo(
    () => isChanged && data.status !== "success" && !isHostSafe,
    [data.status, isChanged, isHostSafe],
  );

  useNextRouterConfirm(isConfirmRequired);

  useEffect(() => {
    update({
      amount: {
        isLoading,
      },
      max: {
        value: balance ? balance.toString() : undefined,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, update, isLoading]);

  useEffect(() => {
    reset(["token"]);
  }, [chainId, reset]);
}
